import React, { useState, useEffect, useCallback } from 'react';

const POLL_INTERVAL = 15000; // Check every 15 seconds

const PendingApproval = () => {
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [status, setStatus] = useState('pending'); // 'pending' | 'approved'
  const [approvedCompany, setApprovedCompany] = useState(null);

  useEffect(() => {
    const data = sessionStorage.getItem('pending_approval');
    if (data) {
      try {
        const parsed = JSON.parse(data);
        setUserEmail(parsed.user_email || '');
        setUserName(parsed.user_name || '');
      } catch {
        // Redirect to login if data is corrupt
        window.location.href = '/login';
      }
    } else {
      window.location.href = '/login';
    }
  }, []);

  const checkStatus = useCallback(async () => {
    try {
      const data = sessionStorage.getItem('pending_approval');
      if (!data) return;
      const { access_token } = JSON.parse(data);
      
      const response = await fetch('/api/auth/pending-status', {
        headers: {
          'Authorization': `Bearer ${access_token}`,
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'approved') {
          setStatus('approved');
          setApprovedCompany(result.company?.name || 'your company');
          sessionStorage.removeItem('pending_approval');
        }
      }
    } catch {
      // Silently retry on next poll
    }
  }, []);

  // Poll for status changes
  useEffect(() => {
    if (status === 'approved') return;
    
    const interval = setInterval(checkStatus, POLL_INTERVAL);
    // Also check immediately on mount
    checkStatus();
    
    return () => clearInterval(interval);
  }, [checkStatus, status]);

  const handleLogout = () => {
    sessionStorage.removeItem('pending_approval');
    window.location.href = '/login';
  };

  const handleLoginAgain = () => {
    sessionStorage.removeItem('pending_approval');
    window.location.href = '/login';
  };

  if (status === 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Approved</h1>
            <p className="text-gray-600 mb-6">
              You've been assigned to <span className="font-semibold">{approvedCompany}</span>. You can now log in and access your applications.
            </p>
            <button
              onClick={handleLoginAgain}
              className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Pending Setup</h1>
          <p className="text-gray-600 mb-6">
            Your account has been created successfully. An administrator needs to assign you to a company and configure your access before you can use the platform.
          </p>

          {(userName || userEmail) && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              {userName && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium text-gray-900">{userName}</span>
                </div>
              )}
              {userEmail && (
                <div className="flex items-center gap-2 text-sm mt-1">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium text-gray-900">{userEmail}</span>
                </div>
              )}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">What happens next?</span>
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>1. An administrator will be notified of your registration</li>
              <li>2. They will assign you to the correct company</li>
              <li>3. This page will automatically update when your account is ready</li>
            </ul>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span>Checking for updates...</span>
          </div>

          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;
